import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import {
  CommissionRule, describeRule, FoundUser, loadSites, lookupUser, NOT_COVERED_BY_ALL, Payee, PRODUCTS, productName, rateProblem,
  RuleDraft, RuleProduct, ruleWho, Scope, SCOPES, scopeName, Site, sportName, SPORTS,
} from '@/results/commission.api';
import { whenText } from '@/results/format';

/** What each level covers, under its name. */
const SCOPE_HINTS: Record<Scope, string> = {
  USER: 'one player',
  UPLINE: "an agent's whole downline",
  WHITELABEL: 'every player of a site',
  GLOBAL: 'every player',
};

/**
 * GS-904 — one commission rule, new or changed (MATCH-ODDS-ENGINE.md §6.7). The operator picks the level (a player, an
 * agent's downline; a site or global for an admin), finds the player or agent by name inside their own tree, the product
 * and sport, then the rate and who receives it; the rule is read back as one sentence before it is saved, with the
 * current rule it replaces. Changing a rule keeps its level, owner, product and sport (that is what "replace" means);
 * the server still checks who may write it and every upline's cap (CAP_EXCEEDED, in its own words).
 */
@Component
export default class RuleDialog extends Vue {
  @Prop({ default: false }) readonly open!: boolean;
  /** The signed-in agent is a commission admin: site-wide and global rules are offered. */
  @Prop({ default: false }) readonly admin!: boolean;
  /** The current rules, to say which one a save replaces. */
  @Prop({ default: (): CommissionRule[] => [] }) readonly rules!: CommissionRule[];
  /** A rule being changed (its key fixed), or null for a new one. */
  @Prop({ default: null }) readonly editing!: CommissionRule | null;
  @Prop({ default: false }) readonly busy!: boolean;
  @Prop({ default: '' }) readonly error!: string;

  private scope: Scope = 'USER';
  private username = '';
  private found: FoundUser | null = null;
  private lookupError = '';
  private looking = false;
  private sites: Site[] = [];
  private siteKey = '';
  private product: RuleProduct = 'MATCH_ODDS';
  private sport = '';
  private enabled = true;
  private rate = '';
  private payee: Payee = 'DIRECT_MANAGER';
  private cap = '';
  private note = '';

  get scopeOptions(): Array<{ key: Scope; label: string; hint: string }> {
    return SCOPES.filter((s) => !s.admin || this.admin).map((s) => ({ key: s.key, label: s.label, hint: SCOPE_HINTS[s.key] }));
  }

  get productOptions(): Array<{ value: string; label: string }> {
    return PRODUCTS.map((p) => ({ value: p.key, label: p.key === 'ALL' ? 'All products (Match Odds only today)' : p.label }));
  }

  get sportOptions(): Array<{ value: string; label: string }> {
    return [{ value: '', label: 'Every sport' }, ...SPORTS.map((s) => ({ value: String(s.id), label: s.label }))];
  }

  get siteOptions(): Array<{ value: string; label: string }> {
    return [{ value: '', label: this.sites.length ? 'Pick the site' : 'Loading sites…' },
      ...this.sites.map((s) => ({ value: String(s.id), label: s.name + (s.dns ? ' — ' + s.dns : '') }))];
  }

  get payeeOptions(): Array<{ value: string; label: string }> {
    const options = [{ value: 'DIRECT_MANAGER', label: "The player's direct manager" }];
    if (this.scope === 'UPLINE') {
      options.push({ value: 'RULE_OWNER', label: 'This agent' + (this.ownerName ? ' (' + this.ownerName + ')' : '') });
    }
    options.push({ value: 'HOUSE', label: 'The house (no agent)' });
    return options;
  }

  get ownerName(): string {
    if (this.editing) {
      return ruleWho(this.editing);
    }
    if (this.scope === 'GLOBAL') {
      return 'Everyone';
    }
    if (this.scope === 'WHITELABEL') {
      const site = this.sites.find((s) => String(s.id) === this.siteKey);
      return site ? site.name : '';
    }
    return this.found ? this.found.username : '';
  }

  get ownerRef(): number | undefined {
    if (this.editing) {
      return this.editing.scopeRef === null ? undefined : this.editing.scopeRef;
    }
    if (this.scope === 'WHITELABEL') {
      return this.siteKey ? Number(this.siteKey) : undefined;
    }
    return this.scope === 'GLOBAL' || !this.found ? undefined : this.found.id;
  }

  /** A player's rule is USER, an agent's UPLINE: the server refuses the other way round, so say it here. */
  get kindProblem(): string {
    if (!this.found || this.editing) {
      return '';
    }
    if (this.scope === 'USER' && this.found.kind === 'AGENT') {
      return `${this.found.username} is an agent: an agent's rule covers its whole downline — pick Agent.`;
    }
    if (this.scope === 'UPLINE' && this.found.kind === 'PLAYER') {
      return `${this.found.username} is a player: pick Player.`;
    }
    return '';
  }

  get ownerReady(): boolean {
    if (this.editing || this.scope === 'GLOBAL') {
      return true;
    }
    if (this.scope === 'WHITELABEL') {
      return !!this.siteKey;
    }
    return !!this.found && !this.kindProblem;
  }

  get rateError(): string {
    return this.enabled ? rateProblem(this.rate) : '';
  }

  get capError(): string {
    return this.scope === 'UPLINE' && this.cap.trim() ? rateProblem(this.cap, 'The cap') : '';
  }

  get allNote(): string {
    return this.product === 'ALL' ? `All never reaches ${NOT_COVERED_BY_ALL.map(productName).join(', ').toLowerCase()}: each needs its own rule.` : '';
  }

  get draft(): RuleDraft {
    const d: RuleDraft = { scope: this.scope, product: this.product, enabled: this.enabled, payee: this.payee };
    if (this.ownerRef !== undefined) {
      d.scopeRef = this.ownerRef;
    }
    if (this.sport) {
      d.sportId = Number(this.sport);
    }
    if (this.enabled) {
      d.ratePct = this.rate.trim();
    }
    if (this.scope === 'UPLINE' && this.cap.trim()) {
      d.downlineCapPct = this.cap.trim();
    }
    if (this.note.trim()) {
      d.note = this.note.trim();
    }
    return d;
  }

  get preview(): string {
    if (!this.ownerReady || (this.enabled && (!this.rate.trim() || this.rateError))) {
      return '';
    }
    return describeRule({ scope: this.scope, enabled: this.enabled, ratePct: this.rate, product: this.product,
      sportId: this.sport ? Number(this.sport) : null, payee: this.payee, downlineCapPct: this.cap.trim() || null }, this.ownerName);
  }

  /** The current rule with the same level, owner, product and sport — the one a save closes. */
  get replaces(): string {
    const ref = this.ownerRef === undefined ? null : this.ownerRef;
    const sport = this.sport ? Number(this.sport) : null;
    const now = this.rules.find((r) => r.current && r.scope === this.scope && r.scopeRef === ref && r.product === this.product
      && r.sportId === sport);
    if (!now) {
      return this.ownerReady ? 'A new rule: nothing at this level is replaced.' : '';
    }
    return `Replaces the current rule: ${now.enabled ? Number(now.ratePct).toFixed(2) + '%' : 'no commission'}, since `
      + `${whenText(now.effectiveFrom)}${now.createdByName ? ' (' + now.createdByName + ')' : ''}.`;
  }

  get canSave(): boolean {
    return !this.busy && this.ownerReady && !this.rateError && !this.capError;
  }

  @Watch('open')
  onOpen(open: boolean): void {
    if (!open) {
      return;
    }
    const r = this.editing;
    this.scope = r ? r.scope : 'USER';
    this.username = r && r.username ? r.username : '';
    this.found = null;
    this.lookupError = '';
    this.siteKey = r && r.scope === 'WHITELABEL' && r.scopeRef !== null ? String(r.scopeRef) : '';
    this.product = r ? r.product : 'MATCH_ODDS';
    this.sport = r && r.sportId !== null ? String(r.sportId) : '';
    this.enabled = r ? r.enabled : true;
    this.rate = r && r.enabled ? String(Number(r.ratePct)) : '';
    this.payee = r ? r.payee : 'DIRECT_MANAGER';
    this.cap = r && r.downlineCapPct ? String(Number(r.downlineCapPct)) : '';
    this.note = '';
  }

  setScope(key: Scope): void {
    this.scope = key;
    this.found = null;
    this.lookupError = '';
    if (key !== 'UPLINE' && this.payee === 'RULE_OWNER') {
      this.payee = 'DIRECT_MANAGER';
    }
    if (key === 'WHITELABEL' && !this.sites.length) {
      loadSites().then((sites) => {
        this.sites = sites;
      }).catch((e: Error) => {
        this.lookupError = e.message;
      });
    }
  }

  setUsername(value: string): void {
    this.username = value;
    this.found = null;
    this.lookupError = '';
  }

  async find(): Promise<void> {
    const name = this.username.trim();
    if (!name || this.looking) {
      return;
    }
    this.looking = true;
    this.lookupError = '';
    try {
      this.found = await lookupUser(name);
    } catch (e) {
      this.found = null;
      this.lookupError = (e as Error).message;
    } finally {
      this.looking = false;
    }
  }

  cancel(): void {
    if (!this.busy) {
      this.$emit('cancel');
    }
  }

  save(): void {
    if (this.canSave) {
      this.$emit('save', this.draft);
    }
  }

  // words for the template
  productName(p: RuleProduct): string {
    return productName(p);
  }

  sportName(id: number | null): string {
    return sportName(id);
  }

  scopeName(s: Scope): string {
    return scopeName(s);
  }
}
